import type {
	SectionPlanInfo,
	SectionType,
	SectionTypeInput,
	SectionTypeRepetitionModel,
} from '@az3fr/core';
import { sectionTypeModels } from '@az3fr/core';

import {
	formatDuration,
	generateDurationOptions,
	generateIntervalOptions,
	generateRepetitionOptions,
} from '@/lib/option-generators';

type GenerateSectionInputOptions =
	| typeof generateRepetitionOptions
	| typeof generateIntervalOptions
	| typeof generateDurationOptions;

interface SectionInputConfig {
	name: SectionTypeInput;
	// The UI label for the input (derived from core model)
	label: string;
	// the name of the corresponding column in the `section_plan` table
	column: keyof SectionPlanInfo;
	// the unit of the input (derived from core model)
	unit: string | null;
	// the options for the input
	optionsGenerator: GenerateSectionInputOptions;
	labelGenerator: (value: number) => string;
}

/**
 * Defines all the display properties, documentation, and configuration
 * rules for a given SectionType. The configuration is derived from the core
 * SectionTypeRepetitionModel, while display info is UI-specific.
 */
interface SectionTypeDisplayInfo {
	// Core model (source of truth for business logic)
	coreModel: SectionTypeRepetitionModel;

	// UI-specific display information
	displayName: string;
	tagline: string;
	description: string;
	longDescription: string;
	displayOrder: number;
	examples: readonly string[];

	// Derived configuration for UI inputs (generated from coreModel)
	inputConfigs: SectionInputConfig[];
}

// Helper function to convert core input to UI input config
function createInputConfig(coreInput: {
	name: SectionTypeInput;
	label: string;
}): SectionInputConfig {
	const getColumnName = (inputName: SectionTypeInput): keyof SectionPlanInfo => {
		switch (inputName) {
			case 'rounds':
				return 'rounds';
			case 'durationSeconds':
				return 'durationSeconds';
			case 'intervalSeconds':
				return 'intervalSeconds';
			default:
				throw new Error(`Unknown input name: ${inputName}`);
		}
	};

	const getOptionsGenerator = (inputName: SectionTypeInput): GenerateSectionInputOptions => {
		switch (inputName) {
			case 'rounds':
				return generateRepetitionOptions;
			case 'durationSeconds':
				return generateDurationOptions;
			case 'intervalSeconds':
				return generateIntervalOptions;
			default:
				throw new Error(`Unknown input name: ${inputName}`);
		}
	};

	const getLabelGenerator = (inputName: SectionTypeInput): ((value: number) => string) => {
		switch (inputName) {
			case 'rounds':
				return (value) => `${value}`;
			case 'durationSeconds':
			case 'intervalSeconds':
				return formatDuration;
			default:
				throw new Error(`Unknown input name: ${inputName}`);
		}
	};

	const getUnit = (inputName: SectionTypeInput): string | null => {
		switch (inputName) {
			case 'rounds':
				return null;
			case 'durationSeconds':
			case 'intervalSeconds':
				return null; // Unit is handled by the formatter
			default:
				return null;
		}
	};

	if (!coreInput) {
		throw new Error('coreInput cannot be undefined');
	}

	return {
		name: coreInput.name,
		label: coreInput.label,
		column: getColumnName(coreInput.name),
		unit: getUnit(coreInput.name),
		optionsGenerator: getOptionsGenerator(coreInput.name),
		labelGenerator: getLabelGenerator(coreInput.name),
	};
}

const sectionTypeDisplayInfos: Record<SectionType, SectionTypeDisplayInfo> = {
	// ============================================================================
	// STANDARD EXERCISES (formerly single/grouped)
	// ============================================================================
	standard: {
		coreModel: sectionTypeModels.standard,
		displayName: 'Standard',
		tagline: 'One exercise at a time',
		// tagline: 'Classic strength block',
		description: 'Perform all sets of one exercise, then move to the next.',
		longDescription:
			'The classic approach to building strength and muscle. Focus on one exercise at a time to maximize performance and lift heavier. This is the foundation of most traditional training programs.',
		examples: ['Barbell Bench Press: 4 sets of 5 reps', 'Cable Tricep Pushdown: 3 sets of 12 reps'],
		displayOrder: 0,
		inputConfigs: sectionTypeModels.standard.inputs.map(createInputConfig),
	},

	// ============================================================================
	// SUPERSET
	// ============================================================================
	superset: {
		coreModel: sectionTypeModels.superset,
		displayName: 'Superset',
		tagline: 'Exercises paired together',
		description: 'Pair exercises back-to-back with minimal rest.',
		longDescription:
			'Increase workout intensity and save time by performing a set of one exercise immediately followed by a set of another. Rest only after completing all exercises in the superset.',
		examples: [
			'Arm Superset: (10 Bicep Curls + 12 Tricep Pushdowns) x 3 sets',
			'Push/Pull Superset: (8 Dumbbell Bench Press + 10 Dumbbell Rows) x 4 sets',
		],
		displayOrder: 1,
		inputConfigs: sectionTypeModels.superset.inputs.map(createInputConfig),
	},

	// ============================================================================
	// CIRCUIT
	// ============================================================================
	circuit: {
		coreModel: sectionTypeModels.circuit,
		displayName: 'Circuit',
		tagline: 'Exercises done in rounds',
		description: 'A series of exercises performed one after another for multiple rounds.',
		longDescription:
			'A great way to build conditioning and muscular endurance. Complete one set of each exercise in the circuit to finish a round, then rest and repeat for the total number of rounds.',
		examples: [
			'Bodyweight Circuit: (10 Push-ups → 15 Squats → 20 Jumping Jacks) x 5 rounds',
			'Kettlebell Circuit: (12 Kettlebell Swings → 8 Goblet Squats) x 10 rounds',
		],
		displayOrder: 2,
		inputConfigs: sectionTypeModels.circuit.inputs.map(createInputConfig),
	},

	// ============================================================================
	// AMRAP
	// ============================================================================
	amrap: {
		coreModel: sectionTypeModels.amrap,
		displayName: 'AMRAP',
		tagline: 'As many rounds as possible before time',
		description: 'As Many Rounds as Possible within a time limit.',
		longDescription:
			'A test of work capacity and pacing. Start a timer and complete as many rounds of the prescribed exercises as you can before the time expires. Your score is the total number of rounds and reps.',
		examples: ['20-Min AMRAP of: 5 Pull-ups, 10 Push-ups, 15 Squats', '7-Min Burpee AMRAP'],
		displayOrder: 3,
		inputConfigs: sectionTypeModels.amrap.inputs.map(createInputConfig),
	},

	// ============================================================================
	// EMOM
	// ============================================================================
	emom: {
		coreModel: sectionTypeModels.emom,
		displayName: 'EMOM',
		tagline: 'A new set starts every interval',
		// tagline: 'Every-minute pacing',
		description: 'A new set starts at the top of a timed interval.',
		longDescription:
			'A powerful tool for building consistency and managing fatigue. At the start of each interval (e.g., every minute), perform your work. The time left until the next interval is your rest. A new round begins with the next interval.',
		examples: [
			'10-Min EMOM: 5 Power Cleans',
			'20-Min EMOM (Alternating): Min 1: 15 Cal Row, Min 2: 12 Burpees',
		],
		displayOrder: 4,
		inputConfigs: sectionTypeModels.emom.inputs.map(createInputConfig),
	},
} as const;

const sectionTypeDisplayInfosArray: SectionTypeDisplayInfo[] = Object.values(
	sectionTypeDisplayInfos,
).sort((a, b) => a.displayOrder - b.displayOrder);

export { sectionTypeDisplayInfos, sectionTypeDisplayInfosArray };
export type { SectionTypeDisplayInfo };
